import type { RequestOptions } from '../client.js';
import type { ChainSlug } from '../types/chains.js';
import {
  type RpcRequest,
  type RpcResponse,
  rpcRequestSchema,
  rpcBatchRequestSchema,
} from '@spectrum-nodes/api-spec/models';
import { ValidationError, ApiError } from '../types/errors.js';
import { parseParams } from '../internals/validation.js';
import { ChainNamespace } from './base.js';

/** Direct JSON-RPC proxy to chain nodes */
export class RpcNamespace extends ChainNamespace {
  /**
   * Send a raw JSON-RPC request to a chain's node.
   * Proxies directly to the node — supports any standard JSON-RPC method
   * (eth_*, trace_*, debug_*, net_*, web3_*, etc.)
   */
  async request(
    chain: ChainSlug | undefined,
    req: RpcRequest,
    options?: RequestOptions,
  ): Promise<RpcResponse> {
    const resolvedChain = this.resolve(chain);
    const validated = parseParams(rpcRequestSchema, req);

    return this.client.rpcProxy(
      resolvedChain,
      { method: validated.method, params: validated.params ?? [], id: validated.id ?? 1 },
      { ...options, cacheTtl: 0 },
    ) as Promise<RpcResponse>;
  }

  /**
   * Send a batch of JSON-RPC requests to a chain's node.
   * Max size is enforced by the shared `MAX_JSON_RPC_BATCH_SIZE`.
   */
  async requestBatch(
    chain: ChainSlug | undefined,
    requests: RpcRequest[],
    options?: RequestOptions,
  ): Promise<RpcResponse[]> {
    const resolvedChain = this.resolve(chain);
    const validated = parseParams(rpcBatchRequestSchema, requests);

    const finalRequests = validated.map((request, index) => ({
      method: request.method,
      params: request.params ?? [],
      id: request.id ?? index + 1,
    }));

    const seenIds = new Set<unknown>();
    for (const r of finalRequests) {
      if (seenIds.has(r.id)) {
        throw new ValidationError(
          'requests',
          `Duplicate id ${String(r.id)} in batch — ids must be unique`,
        );
      }
      seenIds.add(r.id);
    }

    return this.client.rpcProxyBatch(resolvedChain, finalRequests, {
      ...options,
      cacheTtl: 0,
    }) as Promise<RpcResponse[]>;
  }

  /**
   * Shorthand: send a single RPC method and return just the result.
   * Throws if the node returns an error.
   */
  async send<T = unknown>(
    chain: ChainSlug | undefined,
    method: string,
    params?: unknown[],
    options?: RequestOptions,
  ): Promise<T> {
    const response = await this.request(chain, { method, params }, options);

    if ('error' in response && response.error) {
      throw new ApiError(
        `RPC error: ${response.error.message} (code: ${response.error.code})`,
        502,
        this.client.endpointUrl(),
        response.error,
      );
    }

    return (response as { result: T }).result;
  }
}
